import { Link } from 'react-router-dom';
import { Phone, MapPin, Mail, Star, Clock, ArrowRight } from 'lucide-react';

const services = [
  { name: 'Aluminium Partitions', href: '/services#aluminium-partitions' },
  { name: 'Toughened Glass', href: '/services#toughened-glass-partitions' },
  { name: 'ACP Front Elevation', href: '/services#acp-front-elevation' },
  { name: 'Shower Enclosures', href: '/services#shower-enclosures' },
  { name: 'Glass Railings', href: '/services#railings' },
  { name: 'Sliding Systems', href: '/services#sliding-systems' },
];

const quickLinks = [
  { name: 'About Us', href: '/about' },
  { name: 'Our Products', href: '/products' },
  { name: 'Project Gallery', href: '/gallery' },
  { name: 'Customer Reviews', href: '/reviews' },
  { name: 'Contact Us', href: '/contact' },
  { name: 'Get Quote', href: '/contact' },
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white" data-testid="main-footer">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-orange-500 rounded-lg flex items-center justify-center text-white font-bold text-xl font-['Oswald']">
                R
              </div>
              <div>
                <h3 className="font-['Oswald'] font-bold text-xl">RAJ ALUMINUM</h3>
                <p className="text-sm text-slate-400">Glass & Hardware</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Premium aluminium, glass & front elevation solutions in Faridabad. 
              We specialize in residential and commercial interior & exterior glass 
              and aluminium works with quality materials and expert craftsmanship.
            </p>
            <div className="flex items-center gap-2 px-4 py-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
              <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
              <span className="text-yellow-400 font-semibold">5.0</span>
              <span className="text-slate-400">Google Reviews</span>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-['Oswald'] font-semibold text-lg mb-6 flex items-center gap-2">
              <span className="w-8 h-1 bg-orange-500 rounded"></span>
              Our Services
            </h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.href}
                    className="text-slate-400 hover:text-orange-400 transition-colors text-sm flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-['Oswald'] font-semibold text-lg mb-6 flex items-center gap-2">
              <span className="w-8 h-1 bg-sky-500 rounded"></span>
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-sky-400 transition-colors text-sm flex items-center gap-2 group"
                  >
                    <ArrowRight className="w-4 h-4 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-['Oswald'] font-semibold text-lg mb-6 flex items-center gap-2">
              <span className="w-8 h-1 bg-green-500 rounded"></span>
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:08512853955"
                  className="flex items-start gap-3 text-slate-400 hover:text-white transition-colors group"
                >
                  <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center group-hover:bg-orange-500 transition-colors">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Call Us</p>
                    <p className="text-white font-medium">085128 53955</p>
                  </div>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-slate-400">
                  <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Visit Us</p>
                    <p className="text-white text-sm">Chungi no:- 5, Mohana Rd,<br />near Bikaner sweets, Ballabhgarh,<br />Faridabad - 121004</p>
                  </div>
                </div>
              </li>
              <li>
                <div className="flex items-start gap-3 text-slate-400">
                  <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Business Hours</p>
                    <p className="text-white text-sm">Mon - Sat: 8 AM - 8 PM</p>
                    <p className="text-slate-500 text-sm">Sunday: Closed</p>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm text-center md:text-left">
              © {currentYear} Raj Aluminum & Glass Hardware. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span>Aluminium Work in Faridabad</span>
              <span className="hidden md:inline">|</span>
              <span className="hidden md:inline">Glass Work in Ballabhgarh</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
